/**
 * NextRunCell — two-line summary of a connector's next scheduled run.
 *
 *   enabled + nextRunAt   → "<relative>" over the cron description
 *   enabled + no next run → "—" over the cron description (or nothing)
 *   disabled              → "Paused" over "schedule off" (muted)
 */
import React from 'react';
import { describeCron } from '@/lib/cron/describe';

export interface NextRunCellProps {
  nextRunAt: string | null | undefined;
  cron: string | undefined;
  enabled: boolean;
  tz?: string;
}

function relativeFuture(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

export const NextRunCell: React.FC<NextRunCellProps> = ({ nextRunAt, cron, enabled, tz }) => {
  // The shared describer is total, so the "no schedule" case is guarded here —
  // this is the only surface that renders nothing rather than a description.
  //
  // The connector-list payload now carries schedule_timezone alongside
  // schedule_cron (#1091), so the real per-connector zone is passed through.
  // useConnectors normalizes the backend's null to undefined, so an absent zone
  // falls back to the describer's app default — correct for a connector with no
  // schedule row, where cron is absent too and cronText is null anyway.
  const cronText = cron?.trim() ? describeCron(cron, tz) : null;

  if (!enabled) {
    return (
      <span data-mode="paused" className="text-muted-foreground">
        <span className="block leading-tight">Paused</span>
        <span className="block text-xs leading-tight">schedule off</span>
      </span>
    );
  }

  return (
    <span data-mode="scheduled">
      <span className="block leading-tight">{nextRunAt ? relativeFuture(nextRunAt) : '—'}</span>
      {cronText && <span className="block text-xs text-muted-foreground leading-tight">{cronText}</span>}
    </span>
  );
};
